import React from "react";
import ListEditor from "../presupuesto/ListEditor";
import { CAPITULOS_TOGGLEABLES } from "../../utils/reglamentoConfig";
import { colors } from "../../utils/designTokens";

// Formulario de configuracion del reglamento. Vive en el panel derecho de la pantalla.
// Recibe el config completo y un setter; cualquier cambio se propaga al preview en vivo.
//
// Props:
//   config:        ReglamentoConfig
//   onChange:      (nextConfig) => void
//   onGenerarPdf:  () => Promise<void>
//   generating:    boolean

export default function ReglamentoForm({ config, onChange, onGenerarPdf, generating = false }) {
  const c = config;

  const setField = (key, value) => onChange({ ...c, [key]: value });
  const setArray = (key) => (next) => onChange({ ...c, [key]: next });
  const toggleCap = (key) => onChange({
    ...c,
    capitulosVisibles: { ...c.capitulosVisibles, [key]: !c.capitulosVisibles[key] },
  });
  const setHorario = (key) => (next) => onChange({
    ...c,
    horarios: { ...c.horarios, [key]: next },
  });

  return (
    <aside style={S.panel} aria-label="Configurar reglamento">
      <header style={S.header}>
        <h2 style={S.headerTitle}>Configurar reglamento</h2>
        <p style={S.headerSub}>Los cambios se reflejan al instante en la vista previa.</p>
      </header>

      {/* ---------- DATOS GENERALES ---------- */}
      <Section title="Datos del edificio">
        <Text label="Edificio"          value={c.edificio}        onChange={(v) => setField("edificio", v)} />
        <Text label="Padrón"            value={c.padron}          onChange={(v) => setField("padron", v)} />
        <Text label="Vigencia desde"    value={c.vigenciaDesde}   onChange={(v) => setField("vigenciaDesde", v)} />
        <Text label="Versión"           value={c.version}         onChange={(v) => setField("version", v)} />
        <Text label="Fecha aprobación"  value={c.fechaAprobacion} onChange={(v) => setField("fechaAprobacion", v)} />
        <Text label="Administración"    value={c.administracion}  onChange={(v) => setField("administracion", v)} />
        <Text label="Contacto"          value={c.contacto} type="email" onChange={(v) => setField("contacto", v)} />
      </Section>

      {/* ---------- CAPITULOS (los 19, todos toggleables) ---------- */}
      <Section title="Capítulos del reglamento">
        <p style={S.helper}>Activá o desactivá cualquier capítulo. Si una página queda sin contenido, se omite del PDF y el índice + numeración se recalculan.</p>
        <div style={S.toggleAllRow}>
          <button
            type="button"
            onClick={() => onChange({
              ...c,
              capitulosVisibles: Object.fromEntries(CAPITULOS_TOGGLEABLES.map((cap) => [cap.key, true])),
            })}
            style={S.toggleAllBtn}
          >
            Activar todos
          </button>
          <button
            type="button"
            onClick={() => onChange({
              ...c,
              capitulosVisibles: Object.fromEntries(CAPITULOS_TOGGLEABLES.map((cap) => [cap.key, false])),
            })}
            style={S.toggleAllBtn}
          >
            Desactivar todos
          </button>
        </div>
        {CAPITULOS_TOGGLEABLES.map((cap) => (
          <Toggle
            key={cap.key}
            label={`Cap. ${cap.capRomano} — ${cap.label}`}
            value={!!c.capitulosVisibles[cap.key]}
            onChange={() => toggleCap(cap.key)}
          />
        ))}
      </Section>

      {/* ---------- HORARIOS ---------- */}
      {c.capitulosVisibles.gimnasio && (
        <Section title="Horarios — Gimnasio">
          <ListEditor
            items={c.horarios.gimnasio}
            fields={HORARIO_FIELDS}
            onChange={setHorario("gimnasio")}
          />
        </Section>
      )}
      {c.capitulosVisibles.barbacoa && (
        <Section title="Horarios — Barbacoa">
          <ListEditor
            items={c.horarios.barbacoa}
            fields={HORARIO_FIELDS}
            onChange={setHorario("barbacoa")}
          />
        </Section>
      )}
      {c.capitulosVisibles.piscina && (
        <Section title="Horarios — Piscina">
          <ListEditor
            items={c.horarios.piscina}
            fields={HORARIO_FIELDS}
            onChange={setHorario("piscina")}
          />
        </Section>
      )}

      {/* ---------- PROHIBICIONES ---------- */}
      <Section title={`Prohibiciones · ${c.prohibiciones.length}`}>
        <p style={S.helper}>Cada item aparece como una entrada de la grilla con la X roja en la página 4.</p>
        <ListEditor
          items={c.prohibiciones}
          fields={[{ key: "texto", type: "text", placeholder: "Texto de la prohibición" }]}
          onChange={setArray("prohibiciones")}
        />
      </Section>

      {/* ---------- SANCIONES ---------- */}
      <Section title={`Sanciones · ${c.sanciones.length}`}>
        <p style={S.helper}>Tabla escalonada de sanciones. Etapa, sanción y descripción.</p>
        <ListEditor
          items={c.sanciones}
          fields={[
            { key: "etapa",   type: "text", placeholder: "Etapa (ej: 2ª)" },
            { key: "sancion", type: "text", placeholder: "Sanción (ej: Multa de 2 UR)" },
            { key: "detalle", type: "text", placeholder: "Detalle / descripción" },
          ]}
          onChange={setArray("sanciones")}
        />
      </Section>

      {/* ---------- ACCION ---------- */}
      {onGenerarPdf && (
        <button
          type="button"
          onClick={onGenerarPdf}
          disabled={generating}
          style={{ ...S.pdfButton, ...(generating ? S.pdfButtonDisabled : {}) }}
        >
          {generating ? "Generando PDF…" : "Descargar PDF"}
        </button>
      )}
    </aside>
  );
}

// ─── Subcomponentes ─────────────────────────────────────────────────────────

function Section({ title, children }) {
  return (
    <section style={S.section}>
      <h3 style={S.sectionTitle}>{title}</h3>
      <div style={S.sectionBody}>{children}</div>
    </section>
  );
}

function Text({ label, value, onChange, type = "text" }) {
  const id = `reg-${label.replace(/\s+/g, "-").toLowerCase()}`;
  return (
    <div style={S.field}>
      <label htmlFor={id} style={S.label}>{label}</label>
      <input
        id={id}
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        style={S.input}
      />
    </div>
  );
}

function Toggle({ label, value, onChange }) {
  return (
    <label style={S.toggleRow}>
      <input
        type="checkbox"
        checked={!!value}
        onChange={(e) => onChange(e.target.checked)}
        style={{ width: 16, height: 16, accentColor: colors.primary, cursor: "pointer" }}
      />
      <span style={{ fontSize: 13, color: colors.gray900 }}>{label}</span>
    </label>
  );
}

const HORARIO_FIELDS = [
  { key: "lbl",  type: "text", placeholder: "Etiqueta (ej: Horario)" },
  { key: "val",  type: "text", placeholder: "Valor (ej: 06 — 24 hs)" },
  { key: "note", type: "text", placeholder: "Nota (opcional)" },
];

// ─── Estilos ─────────────────────────────────────────────────────────────────

const S = {
  panel: {
    width: 360,
    flexShrink: 0,
    background: colors.white,
    borderLeft: `1px solid ${colors.gray200}`,
    overflowY: "auto",
    padding: "20px 18px 32px",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
    fontWeight: 400,
  },
  header: { marginBottom: 18 },
  headerTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 400,
    color: colors.black,
    letterSpacing: "-0.01em",
  },
  headerSub: {
    margin: "4px 0 0",
    fontSize: 12,
    color: colors.gray600,
    fontWeight: 400,
    lineHeight: 1.5,
  },
  section: {
    paddingBottom: 16,
    marginBottom: 16,
    borderBottom: `1px solid ${colors.gray100}`,
  },
  sectionTitle: {
    margin: "0 0 10px",
    fontSize: 11,
    fontWeight: 400,
    color: colors.gray600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },
  sectionBody: { display: "flex", flexDirection: "column", gap: 10 },
  helper: {
    margin: "0 0 8px",
    fontSize: 11,
    color: colors.gray600,
    lineHeight: 1.4,
  },
  field: { display: "flex", flexDirection: "column", gap: 5 },
  label: {
    fontSize: 11,
    color: colors.gray700,
    fontWeight: 400,
  },
  input: {
    border: `1px solid ${colors.gray300}`,
    borderRadius: 6,
    padding: "8px 10px",
    fontSize: 13,
    color: colors.black,
    fontFamily: "inherit",
    fontWeight: 400,
    outline: "none",
  },
  toggleRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    cursor: "pointer",
    padding: "4px 0",
  },
  toggleAllRow: {
    display: "flex",
    gap: 8,
    marginBottom: 10,
  },
  toggleAllBtn: {
    flex: 1,
    background: colors.gray100,
    color: colors.gray700,
    border: `1px solid ${colors.gray200}`,
    borderRadius: 6,
    padding: "6px 8px",
    fontSize: 11,
    fontWeight: 400,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  pdfButton: {
    marginTop: 12,
    width: "100%",
    background: colors.primary,
    color: colors.white,
    border: 0,
    borderRadius: 10,
    padding: "12px 16px",
    fontSize: 14,
    fontWeight: 400,
    cursor: "pointer",
    fontFamily: "inherit",
    letterSpacing: "-0.005em",
    transition: "background 0.15s ease",
  },
  pdfButtonDisabled: {
    background: colors.gray400,
    cursor: "not-allowed",
  },
};
